import { isAdminEndpoint } from "@insite/client-framework/Common/ApiUrlUtils";
import { getValueCaseInsensitive, SafeDictionary } from "@insite/client-framework/Common/Types";
import { PreparedMetadata } from "@insite/client-framework/Common/Utilities/setPageMetadata";
import logger from "@insite/client-framework/Logger";
import { OdpEvent } from "@insite/client-framework/OdpEvents/OdpEvent";
import { RetrievePageResult } from "@insite/client-framework/Services/ContentService";

/**
 * A reference to the `domain` module of Node.
 * It's been deprecated for several years, but (at the time of writing) no replacement has been announced.
 */
let domain:
    | undefined
    | {
          /** The active context--this will switch when there are multiple users with async I/O, such as with API calls. */
          readonly active: {
              /**
               * Insite-specific extensions to the `domain.active` feature.
               * Using `domain.active` this way is not explicitly supported, but seems to work the way we need.
               */
              insiteSession: InsiteSession;
          };
      };

interface InsiteSession {
    /** Ensures that we have the url available when needed for server side rendering. */
    url?: URL;
    redirectTo?: string;
    trackedPromises: Promise<any>[];
    /** Callback for tracking added promises. */
    promiseAddedCallback?: (stack: string) => void;
    headers?: SafeDictionary<string | string[]>;
    /**
     * @deprecated Use headers["cookie"] instead
     */
    cookies?: string | undefined;
    cookiesDictionary?: SafeDictionary<string>;
    /**
     * @deprecated Use headers["user-agent"] instead
     */
    userAgent?: string | undefined;
    statusCode?: number;
    messagesByName: SafeDictionary<string>;
    translationsByKeyword: SafeDictionary<string>;
    displayErrorPage?: true | undefined;
    errorStatusCode?: number;
    isWebCrawler: boolean;
    isShareEntity: boolean;
    pageMetadata?: PreparedMetadata;
    initialPage?:
        | {
              result: RetrievePageResult;
              url: string;
          }
        | undefined;
    odpEvents: OdpEvent[];
    enableSsr: boolean;
    serverSideRenderingCount?: number;
}

export const throwIfClientSide = () => {
    if (!IS_SERVER_SIDE) {
        throw new Error("This API can only be used server-side.");
    }
};

export const serverSiteMessageResolver = (messageName: string) => {
    throwIfClientSide();

    if (!domain) {
        return undefined;
    }

    return getValueCaseInsensitive(domain.active.insiteSession.messagesByName, messageName);
};

export const setServerSiteMessages = (messagesByName: SafeDictionary<string>) => {
    throwIfClientSide();

    if (!domain) {
        throw new Error("Domain not set.");
    }

    domain.active.insiteSession.messagesByName = messagesByName;
};

export const serverTranslationResolver = (keyword: string) => {
    throwIfClientSide();

    if (!domain) {
        return undefined;
    }

    return getValueCaseInsensitive(domain.active.insiteSession.translationsByKeyword, keyword);
};

export const setServerTranslations = (translationsByKeyword: SafeDictionary<string>) => {
    throwIfClientSide();

    if (!domain) {
        throw new Error("Domain not set.");
    }

    domain.active.insiteSession.translationsByKeyword = translationsByKeyword;
};

/** Sets the `domain` module reference for use with session data storage. */
export const setDomain = (newDomain: typeof domain) => {
    throwIfClientSide();

    if (!newDomain) {
        throw new Error("newDomain parameter must be an object.");
    }

    if (domain && domain !== newDomain) {
        // This should never happen, but in theory if it did, we wouldn't be able to ensure all requests will work.
        logger.error("Fatal error: The domain module reference has changed, breaking server-side session tracking.");
        process.exit(1);
    }

    newDomain.active.insiteSession = {
        trackedPromises: [],
        messagesByName: {},
        translationsByKeyword: {},
        isWebCrawler: false,
        isShareEntity: false,
        odpEvents: [],
        enableSsr: false,
    };

    domain = newDomain;
};

class MissingDomainError extends Error {}

/** Gets the server-side session, throws an error if used client-side. */
const getSession = () => {
    throwIfClientSide();

    const data = domain?.active?.insiteSession;
    if (!data) {
        throw new MissingDomainError("`setDomain` must be called before server-side session data can be accessed.");
    }

    return data;
};

export function setDisplayErrorPage(error: any) {
    throwIfClientSide();

    const session = getSession();
    session.errorStatusCode = (error && "status" in error && error.status) || -1;
    session.displayErrorPage = true;
}

export function getDisplayErrorPage() {
    return getSessionValue("displayErrorPage");
}

export function getErrorStatusCode() {
    return getSessionValue("errorStatusCode");
}

export function getInitialPage() {
    return getSessionValue("initialPage");
}

export function setInitialPage(result: RetrievePageResult, url: string) {
    setSessionValue("initialPage", {
        result,
        url,
    });
}

export function clearInitialPage() {
    setSessionValue("initialPage", undefined);
}

export function setStatusCode(statusCode: number) {
    setSessionValue("statusCode", statusCode);
}

export function getStatusCode() {
    return getSessionValue("statusCode");
}

export function setSSRIsWebCrawler(value: boolean) {
    setSessionValue("isWebCrawler", value);
}

export function getSSRIsWebCrawler() {
    return getSessionValue("isWebCrawler") ?? false;
}

export function setIsShareEntity(value: boolean) {
    setSessionValue("isShareEntity", value);
}

export function getIsShareEntity() {
    return getSessionValue("isShareEntity") ?? false;
}

export function addOdpEvent(value: OdpEvent) {
    getSession().odpEvents.push(value);
}

export function getOdpEvents() {
    return getSession().odpEvents;
}

export function setSessionCookies(cookies: SafeDictionary<string> | string | undefined) {
    const cookiesString = typeof cookies === "object" ? convertCookiesToString(cookies) : cookies;

    setSessionValue("cookies", cookiesString);
    if (typeof cookies === "object") {
        setSessionValue("cookiesDictionary", cookies);
    } else if (typeof cookies === "string") {
        const cookiesDictionary: SafeDictionary<string> = {};
        cookies.split(";").forEach(cookie => {
            const splitIndex = cookie.indexOf("=");
            const cookieName = cookie.substring(0, splitIndex).trim();
            const cookieValue = cookie.substring(splitIndex + 1);
            cookiesDictionary[cookieName] = cookieValue;
        });
        setSessionValue("cookiesDictionary", cookiesDictionary);
    }

    const { headers } = getSession();
    if (!headers) {
        logger.warn(
            "setSessionCookies called but headers is undefined. " +
                "This may cause authentication to fail. Ensure setHeaders is called before setSessionCookies.",
        );
        return;
    }
    headers["cookie"] = cookiesString;
}

function convertCookiesToString(cookies: SafeDictionary<string>) {
    let cookiesString = "";
    Object.keys(cookies).forEach(cookieName => {
        cookiesString += `${cookieName}=${cookies[cookieName]}; `;
    });

    return cookiesString;
}

export function getSessionCookies() {
    if (IS_SERVER_SIDE === false) {
        return;
    }

    try {
        return getSession().cookiesDictionary;
    } catch (error) {
        if (error instanceof MissingDomainError) {
            logger.warn(
                "getCookie was called during Server Side Rendering before setDomain. Cookies are not currently available.",
            );
        } else {
            throw error;
        }
    }
}

/** Ensures that we have the url available when needed for server side rendering */
export function setUrl(url: string) {
    if (IS_SERVER_SIDE === false) {
        return;
    }

    const session = getSession();

    if (session.url) {
        // Indicates a program flow problem.
        throw new Error(`URL can only be set once, already set to ${session.url}`);
    }

    session.url = new URL(url);
    session.trackedPromises = [];
}

export function getUrl() {
    return getSessionValue("url");
}

/** The list of tracked promises that are blocking SSR completion. */
export function getTrackedPromises() {
    return getSessionValue("trackedPromises");
}

/** Sets a calback that receives notifications when promises are added. */
export function setPromiseAddedCallback(promiseAddedCallback: (stack: string) => void) {
    setSessionValue("promiseAddedCallback", promiseAddedCallback);
}

export function setHeaders(headers: SafeDictionary<string | string[]>) {
    setSessionValue("headers", headers);
}

export function setServerPageMetadata(metadata: PreparedMetadata) {
    setSessionValue("pageMetadata", metadata);
}

export function getPageMetadata() {
    return getSessionValue("pageMetadata");
}

export function setEnableSsr(value: boolean) {
    setSessionValue("enableSsr", value);
}

export function getEnableSsr() {
    return getSessionValue("enableSsr");
}

/**
 * @deprecated Use setHeaders to store all headers instead
 */
export function setUserAgent(userAgent?: string) {
    setSessionValue("userAgent", userAgent);
    const { headers } = getSession();
    if (!headers) {
        return;
    }
    headers["user-agent"] = userAgent;
}

function getSessionValue<P extends keyof InsiteSession>(property: P) {
    if (!IS_SERVER_SIDE) {
        return;
    }

    return getSession()[property];
}

function setSessionValue<P extends keyof InsiteSession, V extends InsiteSession[P]>(property: P, value: V) {
    if (!IS_SERVER_SIDE) {
        return;
    }

    getSession()[property] = value;
}

/** Used in place of `history.push` when server-side rendering. */
export function redirectTo(location: string) {
    getSession().redirectTo = location;
}

/** Retrieves a redirect destination previously received via `redirectTo`. */
export const getRedirectTo = () => getSession().redirectTo;

/** Adds a promise to the tracking list, blocking SSR return until completion. */
export function addTask(promise: Promise<any>) {
    if (IS_SERVER_SIDE === false) {
        return;
    }

    const promiseAddedCallback = getSession().promiseAddedCallback;
    if (promiseAddedCallback) {
        let stack = new Error().stack;

        if (stack) {
            stack = stack.substring(5).split("    at ").slice(2).join(" ").trim();
            promiseAddedCallback(stack);
        }
    }

    getSession().trackedPromises.push(promise);
}

function normalizeHeaderEntries(headers: HeadersInit | undefined): [string, string][] {
    if (!headers) {
        return [];
    }
    if (Array.isArray(headers)) {
        return headers.map(([key, value]) => [key, value] as [string, string]);
    }
    if (typeof (headers as Headers).forEach === "function") {
        const entries: [string, string][] = [];
        (headers as Headers).forEach((value, key) => entries.push([key, value]));
        return entries;
    }
    return Object.entries(headers as Record<string, string>);
}

export function mergeServerSideRequestHeaders(
    initHeaders: HeadersInit | undefined,
    forwardedHeaders: SafeDictionary<string | string[]> | undefined,
    options: { endpoint: string; sendCookies: boolean; referrer: string; forwardedHost: string },
): Record<string, string> {
    // we need to ensure we don't get duplicated headers that differ by case, that can make CF unhappy
    const merged = new Map<string, string>();

    for (const [key, value] of normalizeHeaderEntries(initHeaders)) {
        merged.set(key.toLowerCase(), value);
    }

    const source = forwardedHeaders ?? {};
    for (const key of Object.keys(source)) {
        const lowerKey = key.toLowerCase();

        // Naively forwarding all headers can conflict with Node/Express behavior.
        // Crawlers have been found sending problematic Accept headers, breaking API calls.
        if (lowerKey === "host" || lowerKey === "origin" || lowerKey === "referer" || lowerKey === "accept") {
            continue;
        }

        // only send the etag headers for the pageByUrl request to dotnet, that request is how we decide if
        // we want to return a 304 down to the client
        // if we get a 304 from other APIs while doing SSR we won't have the data from that request to return to the client
        if (
            (lowerKey === "if-none-match" || lowerKey === "if-modified-since") &&
            !options.endpoint.startsWith("/api/v2/content/pageByUrl")
        ) {
            continue;
        }

        if (lowerKey === "cookie" && !options.sendCookies) {
            continue;
        }

        // init headers win over forwarded headers of the same name
        if (merged.has(lowerKey)) {
            continue;
        }

        const value = source[key];
        if (value === undefined) {
            continue;
        }

        merged.set(lowerKey, Array.isArray(value) ? value.join(", ") : value);
    }

    // node-fetch does not currently support setting referrer, we should fall back to looking for this if we can't find a referrer
    merged.set("x-referer", options.referrer);
    merged.set("x-forwarded-host", options.forwardedHost);

    const result: Record<string, string> = {};
    merged.forEach((value, key) => {
        result[key] = value;
    });
    return result;
}

/** An implementation of the popular `fetch` API with accommodations  for Node-based server-side rendering. */
export const fetch: typeof window.fetch = (input, init) => {
    if (IS_SERVER_SIDE === false) {
        return window.fetch(input, init);
    }

    const { url, headers } = getSession();
    if (!url) {
        throw new Error("`setUrl` must be called before server-side fetch can be used.");
    }

    let preparedInput: typeof input = input;
    let requestUrl: string;
    let sendCookies = false;

    const endpoint = typeof input === "string" ? input : "url" in input ? input.url : input.toString();
    const isAdmin = isAdminEndpoint(endpoint);
    const apiUrlEnvVar = isAdmin ? "ISC_ADMIN_API_URL" : "ISC_STOREFRONT_API_URL";
    let apiUrl = process.env[apiUrlEnvVar]?.trim();

    // this handles the situation where we are listening on two ports and makes sure we call netcore when needed
    if (url.port === process.env.SECONDARY_INGRESS_PORT) {
        apiUrl = url.origin;
    }

    if (!apiUrl) {
        throw new Error(
            `${apiUrlEnvVar} is not set, so the ${
                isAdmin ? "admin" : "storefront"
            } API base URL is unknown and server-side requests cannot be made.`,
        );
    }

    if (typeof preparedInput === "string") {
        if (!preparedInput.toLowerCase().startsWith("http")) {
            preparedInput = apiUrl + (preparedInput.startsWith("/") ? preparedInput : `/${preparedInput}`);
        }
        requestUrl = preparedInput;
    } else if ("url" in preparedInput) {
        if (!preparedInput.url.toLowerCase().startsWith("http")) {
            preparedInput = { ...preparedInput, url: apiUrl + preparedInput.url };
        }
        requestUrl = preparedInput.url;
    } else {
        let url = preparedInput.toString();
        if (!url.toLowerCase().startsWith("http")) {
            url = apiUrl + url;
        }
        requestUrl = url;
    }

    if (
        requestUrl.toLowerCase().startsWith(apiUrl.toLowerCase()) &&
        !endpoint.toLowerCase().startsWith("/api/internal/content/")
    ) {
        sendCookies = true;
    }

    let preparedInit = init;
    if (!preparedInit) {
        preparedInit = {};
    }

    preparedInit.headers = mergeServerSideRequestHeaders(preparedInit.headers, headers, {
        endpoint,
        sendCookies,
        referrer: url.href,
        forwardedHost: url.host,
    });

    // node-fetch does not currently support setting referrer, we should fall back to looking for this if we can't find a referrer
    preparedInit.referrer = url.href;

    logger.debug(`making request ${JSON.stringify(preparedInput)}`);

    return (global as any).fetch(preparedInput, preparedInit);
};
